import { useState, useCallback } from 'react'
import { getAuth } from 'firebase/auth'

const API_URL = import.meta.env.VITE_API_URL || ''

export function useAdminUsers() {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [hasMore, setHasMore] = useState(false)
  const [nextPageToken, setNextPageToken] = useState(null)

  const getToken = async () => {
    return await getAuth().currentUser?.getIdToken()
  }

  const fetchUsers = useCallback(async (options = {}) => {
    const { search, plan, status, append = false } = options
    
    setLoading(true)
    setError(null)

    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')

      const params = new URLSearchParams()
      if (search) params.append('search', search)
      if (plan) params.append('plan', plan)
      if (status) params.append('status', status)
      if (append && nextPageToken) params.append('startAfter', nextPageToken)

      const response = await fetch(`${API_URL}/api/admin/users?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error('Failed to fetch users')
      }

      const data = await response.json()
      
      if (append) {
        setUsers(prev => [...prev, ...data.users])
      } else {
        setUsers(data.users)
      }
      
      setHasMore(data.hasMore)
      setNextPageToken(data.nextPageToken)
    } catch (err) {
      console.error('Error fetching users:', err)
      setError(err)
    } finally {
      setLoading(false)
    }
  }, [nextPageToken])

  const loadMore = useCallback(() => {
    if (hasMore && !loading) {
      fetchUsers({ append: true })
    }
  }, [fetchUsers, hasMore, loading])

  const fetchUser = useCallback(async (userId) => {
    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')

      const response = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error('Failed to fetch user')
      }

      return await response.json()
    } catch (err) {
      console.error('Error fetching user:', err)
      throw err
    }
  }, [])

  const adjustCredits = useCallback(async (userId, amount, reason) => {
    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')

      const response = await fetch(`${API_URL}/api/admin/users/${userId}/credits`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ amount, reason }),
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Failed to adjust credits')
      }

      return await response.json()
    } catch (err) {
      console.error('Error adjusting credits:', err)
      throw err
    }
  }, [])

  const overridePlan = useCallback(async (userId, plan, note) => {
    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')

      const response = await fetch(`${API_URL}/api/admin/users/${userId}/override`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ plan, note }),
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Failed to override plan')
      }

      return await response.json()
    } catch (err) {
      console.error('Error overriding plan:', err)
      throw err
    }
  }, [])

  return {
    users,
    loading,
    error,
    hasMore,
    fetchUsers,
    loadMore,
    fetchUser,
    adjustCredits,
    overridePlan,
  }
}
